import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { captureError } from '@/lib/sentry';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateAnalyseIA } from '@/lib/queryInvalidation';
import {
  BENCHMARKS_NATIONAUX,
  BENCHMARKS_REGIONS,
  getTypeBenchmarkFromPictogramme,
  type MargesParType,
} from '@/data/benchmarksBTP';
import { toast } from 'sonner';
import { getDateFinEffective, AffectationRetard, SuspensionInfo } from '@/lib/dateUtils';
import { format } from 'date-fns';

// ============================================================================
// TYPES
// ============================================================================

export interface AnalyseIAResult {
  score: number;
  etoiles: number;
  verdict: string;
  indicateur: 'exceptionnel' | 'excellent' | 'tres_bien' | 'bien' | 'correct' | 'attention' | 'critique';
  sante_financiere: {
    marge_actuelle_pct: number;
    marge_prevue_pct?: number;
    ecart_pts?: number;
    vs_benchmark: string;
    ecart_vs_benchmark_pts?: number;
    statut: string;
    alerte: string | null;
    analyse?: string;
  };
  sante_planning: {
    avancement_pct: number;
    ecart_jours: number;
    statut: string;
    alerte: string | null;
  };
  completude_donnees?: {
    score: number;
    manquants: string[];
    impact: string;
  };
  analyse_couts?: {
    poste_problematique: string | null;
    ecart_max_pct: number;
    analyse: string;
  };
  points_forts: string[];
  alertes: Array<{
    niveau: 'critique' | 'attention' | 'info';
    titre?: string;
    message: string;
    action_immediate?: string;
    action?: string;
  }>;
  recommandations: Array<{
    priorite: 'haute' | 'moyenne' | 'basse';
    action: string;
    impact: string;
    categorie: string;
  }>;
  kpis: {
    roi_pct: number;
    ca_par_jour: number;
    indice_performance?: number;
    rentabilite_horaire?: number | null;
    cout_journalier_moyen?: number;
    marge_euros?: number;
    marge_pct?: number;
    cout_journalier?: number;
  };
  conseil_expert: string;
}

export interface LimitesAnalyse {
  peutAnalyser: boolean;
  minutesRestantes: number;
  quotaMax: number;
  quotaUtilise: number;
  quotaRestant: number;
}

export interface AnalyseRecord {
  id: string;
  score: number;
  resultat: AnalyseIAResult;
  created_at: string;
  type: 'manuelle' | 'cloture';
}

// Type pour les données chargées
interface DonneesChantierCompletes {
  chantier: {
    id: string;
    nom: string;
    reference: string | null;
    client: string | null;
    adresse: string | null;
    description: string | null;
    statut: string | null;
    pictogramme: string | null;
    region: string | null;
  };
  devis: {
    montant_ht: number;
    montant_ttc: number;
    tva: number;
    version: string;
    statut: string;
  };
  planning: {
    date_debut_prevue: string | null;
    date_debut_reelle: string | null;
    date_fin_prevue: string | null;
    date_fin_reelle: string | null;
    duree_estimee_jours: number;
    jours_ecoules: number;
  };
  depenses: {
    factures: {
      total_ht: number;
      materiaux: number;
      autres: number;
      nb_factures: number;
      detail: Array<{
        fournisseur: string;
        categorie: string;
        montant_ht: number;
        date: string;
      }>;
    };
    frais: {
      total: number;
      par_type: Record<string, number>;
      nb_frais: number;
      detail: Array<{
        type: string;
        description: string;
        montant: number;
        date: string;
      }>;
    };
    main_oeuvre: {
      total: number;
      total_heures: number;
      nb_membres: number;
      cout_horaire_moyen: number;
    };
    total: number;
  };
  equipe: {
    membres: Array<{
      nom: string;
      poste: string;
      specialite: string;
      equipe_nom: string;
      date_debut: string;
      date_fin: string;
      jours_travailles: number;
      heures_par_jour: number;
      heures_total: number;
      taux_horaire: number;
      coefficient_charges: number;
      cout_brut: number;
      cout_total: number;
    }>;
    nb_membres: number;
    total_heures: number;
    total_cout: number;
  };
  facturation: {
    total_facture: number;
    total_paye: number;
    reste_a_facturer: number;
    reste_a_payer: number;
    nb_factures: number;
    nb_paiements: number;
    statut_paiement: string;
    montant_acompte: number;
    montant_paye_simplifie: number;
    pourcentage_encaisse: number;
  };
  metriques: {
    marge_euros: number;
    marge_pct: number;
    budget_disponible: number;
    cout_journalier: number;
    jour_critique: number | null;
    jours_restants_rentables: number | null;
    statut_rentabilite: string;
    // Données de retard
    cout_retard_engage: number;
    cout_retard_previsionnel: number;
    penalites_retard: number;
    cout_journalier_equipe: number;
  };
  // Données de suspensions
  suspensions: {
    nombre: number;
    duree_totale_jours: number;
    cout_remobilisation_total: number;
    actives: number;
    raisons: string[];
    historique: Array<{
      date_suspension: string;
      date_reprise: string | null;
      raison: string;
      raison_detail: string | null;
      duree_jours: number;
      cout_mobilisation: number;
      type_suspension: string;
    }>;
  };
  // Absences des membres de l'équipe pendant le chantier
  absences: {
    total_jours: number;
    membres_avec_absences: number;
    detail: Array<{
      membre_nom: string;
      type_absence: string;
      date_debut: string;
      date_fin: string;
      jours: number;
      motif: string | null;
    }>;
  };
  // Conflits d'affectation (membres affectés sur plusieurs chantiers)
  conflits_affectation: {
    nb_membres_en_conflit: number;
    nb_total_membres: number;
    detail: Array<{
      membre_nom: string;
      autre_chantier_nom: string;
      heures_par_jour: number;
      date_debut: string;
      date_fin: string;
    }>;
  };
  // Budgets par poste (pour la clôture)
  budgets_par_poste: {
    budget_materiaux: number;
    budget_main_oeuvre: number;
    budget_frais: number;
    budget_total: number;
    reel_materiaux: number;
    reel_main_oeuvre: number;
    reel_frais: number;
    reel_total: number;
  } | null;
  // Retards enregistrés (depuis frais_chantier + affectations)
  retards_enregistres: {
    jours_retard: number;
    cout_mo_retard: number;
    penalites_retard: number;
    has_retard: boolean;
    membres_mobilises: Array<{ nom: string; jours: number }>;
  };
  // Sous-traitants
  sous_traitants: {
    nombre: number;
    montant_total_ht: number;
    montant_facture_ht: number;
    reste_a_payer: number;
    detail: Array<{
      nom: string;
      specialite: string;
      montant_contrat_ht: number;
      montant_facture_ht: number;
      reste_a_payer: number;
      pourcentage_paye: number;
      statut: string;
      description: string;
    }>;
  };
}

// ============================================================================
// FONCTION POUR CHARGER TOUTES LES DONNÉES DU CHANTIER (OPTIMISÉE)
// ============================================================================

async function chargerDonneesCompletes(chantierId: string): Promise<DonneesChantierCompletes> {
  if (import.meta.env.DEV) console.log('[useAnalyseIA] 📊 Chargement données complètes...');

  // 1. CHANTIER + DEVIS (requête jointe)
  const { data: chantierRaw, error: errChantier } = await supabase
    .from('chantiers')
    .select(`
      *,
      devis:devis(id, montant_ht, montant_ttc, tva, version, statut, actif)
    `)
    .eq('id', chantierId)
    .single();

  if (errChantier || !chantierRaw) {
    console.error('[useAnalyseIA] ❌ Erreur chantier:', errChantier);
    throw new Error('Chantier non trouvé');
  }

  // Récupérer le devis actif
  const devisActif = chantierRaw.devis?.find((d: any) => d.actif) || chantierRaw.devis?.[0];
  const montantDevisHT = devisActif?.montant_ht || chantierRaw.budget_ht || 0;
  const montantDevisTTC = devisActif?.montant_ttc || 0;
  const tva = devisActif?.tva || 20;

  if (import.meta.env.DEV) {
    console.log('[useAnalyseIA] ✅ Chantier:', chantierRaw.nom_chantier || chantierRaw.nom);
    console.log('[useAnalyseIA] ✅ Devis actif:', montantDevisHT, '€ HT');
  }

  // 2. MÉTRIQUES TEMPS RÉEL (déjà calculées par le système)
  const { data: metricsRaw } = await supabase
    .from('chantier_metrics_realtime')
    .select('*')
    .eq('chantier_id', chantierId)
    .maybeSingle();

  // Extraire les métriques du champ JSONB si nécessaire
  const metrics = metricsRaw?.metrics || metricsRaw || null;
  if (import.meta.env.DEV) console.log('[useAnalyseIA] ✅ Métriques temps réel:', metrics ? 'disponibles' : 'non disponibles');

  // 3. FACTURES FOURNISSEURS (Matériaux + Autres)
  const { data: factures } = await supabase
    .from('factures_fournisseurs')
    .select('*')
    .eq('chantier_id', chantierId);

  const facturesMateriaux = factures?.filter(f => f.categorie === 'Matériaux') || [];
  const facturesAutres = factures?.filter(f => f.categorie !== 'Matériaux') || [];
  const totalFacturesHT = factures?.reduce((sum, f) => sum + Number(f.montant_ht || 0), 0) || 0;
  const totalFacturesMateriaux = facturesMateriaux.reduce((sum, f) => sum + Number(f.montant_ht || 0), 0);
  const totalFacturesAutres = facturesAutres.reduce((sum, f) => sum + Number(f.montant_ht || 0), 0);

  if (import.meta.env.DEV) {
    console.log('[useAnalyseIA] ✅ Factures:', factures?.length || 0, '| Total HT:', totalFacturesHT, '€');
    console.log('[useAnalyseIA]    - Matériaux:', totalFacturesMateriaux, '€ | Autres:', totalFacturesAutres, '€');
    // DEBUG: Voir les catégories réelles des factures
    if (factures && factures.length > 0) {
      console.log('[useAnalyseIA] 🔍 DEBUG Factures détail:', factures.map(f => ({
        fournisseur: f.fournisseur,
        categorie: f.categorie,
        montant_ht: f.montant_ht,
      })));
    }
  }

  // 4. FRAIS CHANTIER (Consommables, Déplacements, etc.)
  const { data: frais } = await supabase
    .from('frais_chantier')
    .select('*')
    .eq('chantier_id', chantierId);

  const totalFrais = frais?.reduce((sum, f) => sum + Number(f.montant_total || 0), 0) || 0;
  const fraisParType = frais?.reduce((acc, f) => {
    const type = f.type_frais || 'Autres';
    acc[type] = (acc[type] || 0) + Number(f.montant_total || 0);
    return acc;
  }, {} as Record<string, number>) || {};

  if (import.meta.env.DEV) {
    console.log('[useAnalyseIA] ✅ Frais:', frais?.length || 0, '| Total:', totalFrais, '€');
    console.log('[useAnalyseIA]    - Par type:', fraisParType);
  }

  // 5. AFFECTATIONS ÉQUIPE (avec détails membres et équipes)
  const { data: affectations } = await supabase
    .from('affectations_chantiers')
    .select(`
      *,
      membre:membres_equipe(id, prenom, nom, poste, specialite, taux_horaire, coefficient_charges, heures_par_jour),
      equipe:equipes(id, nom, specialite)
    `)
    .eq('chantier_id', chantierId);

  // Calculer les coûts MO par membre
  const equipesDetails = (affectations || []).map(a => {
    const tauxHoraire = a.taux_horaire_specifique || a.membre?.taux_horaire || 20;
    const coeffCharges = a.membre?.coefficient_charges || 1.85;
    const heuresParJour = a.heures_par_jour || a.membre?.heures_par_jour || 8;
    const jours = a.jours_travailles || 0;
    const heuresTotal = jours * heuresParJour;
    const coutCharges = heuresTotal * tauxHoraire * coeffCharges;
    // Détecter si c'est une affectation de retard (pour éviter faux positifs sur doublons)
    const estRetard = a.notes?.toLowerCase().includes('retard') || false;

    return {
      nom: a.membre ? `${a.membre.prenom || ''} ${a.membre.nom || ''}`.trim() : 'Membre inconnu',
      poste: a.membre?.poste || 'Non défini',
      specialite: a.membre?.specialite || a.equipe?.specialite || '',
      equipe_nom: a.equipe?.nom || '',
      date_debut: a.date_debut,
      date_fin: a.date_fin,
      jours_travailles: jours,
      heures_par_jour: heuresParJour,
      heures_total: heuresTotal,
      taux_horaire: tauxHoraire,
      coefficient_charges: coeffCharges,
      cout_brut: Math.round(heuresTotal * tauxHoraire * 100) / 100,
      cout_total: Math.round(coutCharges * 100) / 100,
      est_retard: estRetard, // Flag pour distinguer affectations normales vs retard
    };
  });

  const totalMO = equipesDetails.reduce((sum, e) => sum + e.cout_total, 0);
  const totalHeures = equipesDetails.reduce((sum, e) => sum + e.heures_total, 0);
  const nbMembres = equipesDetails.length;

  // 5b. CHARGER RETARDS ET SUSPENSIONS POUR DATE FIN EFFECTIVE
  const { data: affectationsRetardRaw } = await supabase
    .from('affectations_chantiers')
    .select('date_fin, jours_travailles, jours_details')
    .eq('chantier_id', chantierId)
    .like('notes', 'Retard chantier%');

  const { data: suspensionsRaw } = await supabase
    .from('suspensions_chantiers')
    .select('date_suspension, date_reprise, duree_suspension_jours')
    .eq('chantier_id', chantierId)
    .not('date_reprise', 'is', null);

  // Convertir pour getDateFinEffective
  const affectationsRetard: AffectationRetard[] = (affectationsRetardRaw || []).map(a => ({
    date_fin: a.date_fin,
    jours_travailles: a.jours_travailles || 0,
    jours_details: a.jours_details as AffectationRetard['jours_details']
  }));

  // Calculer jours_retard_travailles depuis les affectations retard (dédupliqués)
  const datesRetardUniques = new Set<string>();
  affectationsRetardRaw?.forEach(a => {
    if (a.jours_details && Array.isArray(a.jours_details)) {
      (a.jours_details as Array<{ date: string; period?: string }>).forEach((jd) => {
        datesRetardUniques.add(`${jd.date}:${jd.period || 'FULL'}`);
      });
    }
  });

  let joursRetardTravailles = 0;
  datesRetardUniques.forEach(key => {
    const period = key.split(':')[1];
    joursRetardTravailles += (period === 'AM' || period === 'PM') ? 0.5 : 1;
  });

  if (import.meta.env.DEV) console.log('[useAnalyseIA] ✅ Jours retard travaillés (dédupliqués):', joursRetardTravailles);

  const suspensionsTerminees: SuspensionInfo[] = (suspensionsRaw || []).map(s => ({
    date_suspension: s.date_suspension,
    date_reprise: s.date_reprise,
    duree_suspension_jours: s.duree_suspension_jours
  }));

  // Calculer la date de fin effective
  const dateFinEffective = getDateFinEffective({
    date_fin_estimee: chantierRaw.date_fin_estimee,
    date_fin_initiale: chantierRaw.date_fin_initiale,
    affectationsRetard,
    suspensionsTerminees
  });

  const dateFinEffectiveStr = dateFinEffective ? format(dateFinEffective, 'yyyy-MM-dd') : null;

  if (import.meta.env.DEV) console.log('[useAnalyseIA] ✅ Date fin effective:', dateFinEffectiveStr || chantierRaw.date_fin_estimee);

  // Extraire les IDs des membres pour les requêtes absences et conflits
  const membresIds = (affectations || [])
    .map(a => a.membre?.id)
    .filter((id): id is string => !!id);

  if (import.meta.env.DEV) {
    console.log('[useAnalyseIA] ✅ Équipe:', nbMembres, 'membres |', totalHeures, 'h | Total MO:', totalMO, '€');
    equipesDetails.forEach(e => {
      console.log(`[useAnalyseIA]    - ${e.nom} (${e.poste}): ${e.jours_travailles}j × ${e.heures_par_jour}h = ${e.heures_total}h → ${e.cout_total}€`);
    });
  }

  // 6. FACTURES CLIENTS + PAIEMENTS — Source unique via RPC get_paiement_totals
  const { data: facturesClients } = await supabase
    .from('factures_clients')
    .select('*')
    .eq('chantier_id', chantierId);

  const { data: paiements } = await supabase
    .from('paiements_clients')
    .select('*')
    .eq('chantier_id', chantierId);

  // Utiliser le RPC comme source de vérité pour les totaux encaissés
  const { data: paiementTotalsData } = await supabase
    .rpc('get_paiement_totals', { p_chantier_id: chantierId });
  const pt = paiementTotalsData?.[0];

  const totalFacture = facturesClients?.reduce((sum, f) => sum + Number(f.montant_ht || 0), 0) || 0;
  // Utiliser le total encaissé du RPC (filtré par statut='encaisse') au lieu de la somme brute
  const totalPaye = pt?.total_encaisse ?? (paiements?.reduce((sum, p) => sum + Number(p.montant || 0), 0) || 0);
  const resteAFacturer = montantDevisHT - totalFacture;
  const resteAPayer = (pt?.total_a_encaisser ?? montantDevisHT) - totalPaye;

  if (import.meta.env.DEV) console.log('[useAnalyseIA] ✅ Facturation client:', totalFacture, '€ facturé |', totalPaye, '€ encaissé (RPC) | %:', pt?.pourcentage_encaisse);

  // 7. CALCULS FINAUX
  const totalDepenses = totalFacturesHT + totalFrais + totalMO;
  const margeReelle = montantDevisHT - totalDepenses;
  const tauxMargeReel = montantDevisHT > 0 ? (margeReelle / montantDevisHT) * 100 : 0;

  // Utiliser les métriques temps réel si disponibles (plus fiables)
  const margeFinale = metrics?.marge_finale ?? margeReelle;
  const tauxMargeFinal = metrics?.marge_finale_pct ?? tauxMargeReel;

  // Calculer jours écoulés
  const dateDebut = chantierRaw.date_debut_reelle || chantierRaw.date_debut_prevue;
  let joursEcoules = 0;
  if (dateDebut) {
    const debut = new Date(dateDebut);
    const aujourdhui = new Date();
    joursEcoules = Math.max(0, Math.ceil((aujourdhui.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24)));
  }

  if (import.meta.env.DEV) {
    console.log('[useAnalyseIA] ═══════════════════════════════════════');
    console.log('[useAnalyseIA] 📊 RÉSUMÉ FINANCIER:');
    console.log('[useAnalyseIA]    Devis HT:', montantDevisHT, '€');
    console.log('[useAnalyseIA]    Factures fournisseurs:', totalFacturesHT, '€');
    console.log('[useAnalyseIA]    Frais annexes:', totalFrais, '€');
    console.log('[useAnalyseIA]    Main d\'œuvre:', totalMO, '€');
    console.log('[useAnalyseIA]    TOTAL DÉPENSES:', totalDepenses, '€');
    console.log('[useAnalyseIA]    MARGE:', margeFinale.toFixed(2), '€ (', tauxMargeFinal.toFixed(1), '%)');
    console.log('[useAnalyseIA] ═══════════════════════════════════════');
  }

  return {
    // Infos chantier
    chantier: {
      id: chantierRaw.id,
      nom: chantierRaw.nom_chantier || chantierRaw.nom || 'Sans nom',
      reference: chantierRaw.reference_chantier,
      client: chantierRaw.client,
      adresse: chantierRaw.adresse,
      description: chantierRaw.description,
      statut: chantierRaw.etat_chantier || chantierRaw.statut,
      pictogramme: chantierRaw.pictogramme,
      region: chantierRaw.region,
    },

    // Devis
    devis: {
      montant_ht: montantDevisHT,
      montant_ttc: montantDevisTTC,
      tva: tva,
      version: devisActif?.version || 'V1',
      statut: devisActif?.statut || 'brouillon',
    },

    // Planning - utilise dateFinEffective qui inclut retards et suspensions
    planning: {
      date_debut_prevue: chantierRaw.date_debut_prevue,
      date_debut_reelle: chantierRaw.date_debut_reelle || chantierRaw.date_debut_prevue,
      date_fin_prevue: dateFinEffectiveStr || chantierRaw.date_fin_prevue || chantierRaw.date_fin_estimee,
      date_fin_reelle: chantierRaw.date_fin_reelle,
      duree_estimee_jours: chantierRaw.duree_estimee_jours || metrics?.duree_estimee_jours || 0,
      jours_ecoules: metrics?.jours_ecoules || joursEcoules,
    },

    // Dépenses détaillées
    depenses: {
      factures: {
        total_ht: totalFacturesHT,
        materiaux: totalFacturesMateriaux,
        autres: totalFacturesAutres,
        nb_factures: factures?.length || 0,
        detail: (factures || []).map(f => ({
          fournisseur: f.fournisseur || 'Inconnu',
          categorie: f.categorie || 'Autre',
          montant_ht: Number(f.montant_ht || 0),
          date: f.date_facture || '',
        })),
      },
      frais: {
        total: totalFrais,
        par_type: fraisParType,
        nb_frais: frais?.length || 0,
        detail: (frais || []).map(f => ({
          type: f.type_frais || 'Autre',
          description: f.description || '',
          montant: Number(f.montant_total || 0),
          date: f.date_frais || '',
        })),
      },
      main_oeuvre: {
        total: totalMO,
        total_heures: totalHeures,
        nb_membres: nbMembres,
        cout_horaire_moyen: totalHeures > 0 ? Math.round(totalMO / totalHeures * 100) / 100 : 0,
      },
      total: totalDepenses,
    },

    // Équipe détaillée
    equipe: {
      membres: equipesDetails,
      nb_membres: nbMembres,
      total_heures: totalHeures,
      total_cout: totalMO,
    },

    // Facturation client — utilise le RPC get_paiement_totals comme source de vérité
    facturation: {
      total_facture: totalFacture,
      total_paye: totalPaye,
      reste_a_facturer: resteAFacturer,
      reste_a_payer: resteAPayer,
      nb_factures: facturesClients?.length || 0,
      nb_paiements: paiements?.length || 0,
      statut_paiement: pt?.statut_paiement || chantierRaw.statut_paiement || 'non_facture',
      montant_acompte: Number(chantierRaw.montant_acompte) || 0,
      montant_paye_simplifie: Number(chantierRaw.montant_paye) || 0,
      pourcentage_encaisse: pt?.pourcentage_encaisse ?? 0,
    },

    // Métriques calculées (temps réel)
    metriques: {
      marge_euros: margeFinale,
      marge_pct: tauxMargeFinal,
      budget_disponible: metrics?.budget_disponible ?? (montantDevisHT - totalDepenses),
      // Coût journalier = dépenses totales / jours écoulés (ou durée estimée si terminé)
      cout_journalier: metrics?.cout_journalier_equipe || (joursEcoules > 0 ? Math.round(totalDepenses / joursEcoules) : 0),
      jour_critique: metrics?.jour_critique || null,
      jours_restants_rentables: metrics?.jours_restants_rentables || null,
      statut_rentabilite: metrics?.statut_rentabilite || 'VERT',
      // Données de retard (depuis metrics temps réel)
      cout_retard_engage: metrics?.cout_retard_engage || 0,
      cout_retard_previsionnel: metrics?.cout_retard_previsionnel || 0,
      penalites_retard: metrics?.penalites_retard || 0,
      cout_journalier_equipe: metrics?.cout_journalier_equipe || 0,
      // Jours de retard travaillés (calculés depuis affectations retard, dédupliqués)
      jours_retard_travailles: joursRetardTravailles,
    },

    // Données de suspensions (chargées séparément)
    suspensions: await chargerSuspensions(chantierId),

    // Absences des membres de l'équipe pendant le chantier
    absences: await chargerAbsences(
      membresIds,
      chantierRaw.date_debut_reelle || chantierRaw.date_debut_prevue,
      chantierRaw.date_fin_reelle || chantierRaw.date_fin_prevue
    ),

    // Conflits d'affectation
    conflits_affectation: await chargerConflitsAffectation(
      chantierId,
      membresIds,
      chantierRaw.date_debut_reelle || chantierRaw.date_debut_prevue,
      chantierRaw.date_fin_reelle || chantierRaw.date_fin_prevue
    ),

    // Budgets par poste (pour la clôture)
    budgets_par_poste: await chargerBudgetsParPoste(
      chantierId,
      totalFacturesHT, // Matériaux + autres factures
      totalMO,
      totalFrais,
      montantDevisHT
    ),

    // Retards enregistrés (depuis frais_chantier + affectations)
    retards_enregistres: await chargerRetardsEnregistres(chantierId),

    // Sous-traitants
    sous_traitants: await chargerSousTraitants(chantierId),
  };
}

// Helper pour charger les suspensions
async function chargerSuspensions(chantierId: string) {
  const { data: suspensions } = await supabase
    .from('suspensions_chantiers')
    .select('*')
    .eq('chantier_id', chantierId)
    .order('date_suspension', { ascending: false });

  if (!suspensions || suspensions.length === 0) {
    return {
      nombre: 0,
      duree_totale_jours: 0,
      cout_remobilisation_total: 0,
      actives: 0,
      raisons: [] as string[],
      historique: [] as Array<{
        date_suspension: string;
        date_reprise: string | null;
        raison: string;
        raison_detail: string | null;
        duree_jours: number;
        cout_mobilisation: number;
        type_suspension: string;
      }>,
    };
  }

  return {
    nombre: suspensions.length,
    duree_totale_jours: suspensions.reduce((sum, s) => sum + (s.duree_suspension_jours || 0), 0),
    cout_remobilisation_total: suspensions.reduce((sum, s) => sum + (s.cout_mobilisation || 0), 0),
    actives: suspensions.filter(s => !s.date_reprise).length,
    raisons: [...new Set(suspensions.map(s => s.raison).filter(Boolean))] as string[],
    historique: suspensions.map(s => ({
      date_suspension: s.date_suspension,
      date_reprise: s.date_reprise,
      raison: s.raison || 'autre',
      raison_detail: s.raison_detail,
      duree_jours: s.duree_suspension_jours || 0,
      cout_mobilisation: s.cout_mobilisation || 0,
      type_suspension: s.type_suspension || 'totale',
    })),
  };
}

// Helper pour charger les retards enregistrés (depuis frais_chantier + affectations)
async function chargerRetardsEnregistres(chantierId: string) {
  // Chercher les affectations de retard (notes commence par "Retard chantier")
  const { data: affectationsRetard } = await supabase
    .from('affectations_chantiers')
    .select(`
      jours_travailles,
      heures_par_jour,
      taux_horaire_specifique,
      notes,
      membre:membres_equipe(prenom, nom, taux_horaire, coefficient_charges, heures_par_jour)
    `)
    .eq('chantier_id', chantierId)
    .like('notes', 'Retard chantier%');

  // Chercher les pénalités de retard dans frais_chantier
  const { data: fraisRetard } = await supabase
    .from('frais_chantier')
    .select('montant_total, type_frais')
    .eq('chantier_id', chantierId)
    .or('type_frais.ilike.%pénalité%retard%,type_frais.ilike.%penalité%retard%');

  // Calculer le nombre de jours de retard (max des affectations)
  const joursRetard = affectationsRetard?.reduce((max, a) =>
    Math.max(max, a.jours_travailles || 0), 0) || 0;

  // Calculer le coût MO des retards
  const coutMO = (affectationsRetard || []).reduce((sum, a) => {
    const membre = a.membre as { prenom: string; nom: string; taux_horaire: number; coefficient_charges: number; heures_par_jour: number } | null;
    const taux = a.taux_horaire_specifique || membre?.taux_horaire || 0;
    const coeffCharges = membre?.coefficient_charges || 1.85;
    const heures = (a.jours_travailles || 0) * (a.heures_par_jour || membre?.heures_par_jour || 8);
    return sum + (heures * taux * coeffCharges);
  }, 0);

  // Calculer les pénalités contractuelles
  const penalites = (fraisRetard || []).reduce((sum, f) =>
    sum + Number(f.montant_total || 0), 0);

  // Liste des membres mobilisés pour le retard
  const membresMobilises = (affectationsRetard || []).map(a => {
    const membre = a.membre as { prenom: string; nom: string } | null;
    return {
      nom: membre ? `${membre.prenom} ${membre.nom}`.trim() : 'Inconnu',
      jours: a.jours_travailles || 0,
    };
  });

  if (import.meta.env.DEV) console.log('[useAnalyseIA] ✅ Retards enregistrés:', joursRetard, 'jours | Coût MO:', Math.round(coutMO), '€ | Pénalités:', penalites, '€');

  return {
    jours_retard: joursRetard,
    cout_mo_retard: Math.round(coutMO),
    penalites_retard: penalites,
    has_retard: joursRetard > 0 || (affectationsRetard?.length || 0) > 0,
    membres_mobilises: membresMobilises,
  };
}

// Helper pour charger les données des sous-traitants avec montants facturés
async function chargerSousTraitants(chantierId: string) {
  const { data: contrats } = await supabase
    .from('contrats_sous_traitance')
    .select(`
      id,
      montant_contrat_ht,
      statut,
      date_debut_prevue,
      date_fin_prevue,
      description_travaux,
      sous_traitant:sous_traitants(nom, specialite)
    `)
    .eq('chantier_id', chantierId);

  if (!contrats || contrats.length === 0) {
    return {
      nombre: 0,
      montant_total_ht: 0,
      montant_facture_ht: 0,
      reste_a_payer: 0,
      detail: [] as Array<{
        nom: string;
        specialite: string;
        montant_contrat_ht: number;
        montant_facture_ht: number;
        reste_a_payer: number;
        pourcentage_paye: number;
        statut: string;
        description: string;
      }>,
    };
  }

  // Charger les factures/frais liés aux contrats ST depuis frais_chantier
  const contratIds = contrats.map(c => c.id);
  const fraisParContrat: Record<string, number> = {};

  if (contratIds.length > 0) {
    const { data: fraisData } = await supabase
      .from('frais_chantier')
      .select('contrat_st_id, montant_ht')
      .in('contrat_st_id', contratIds);

    // Agréger les montants facturés par contrat
    (fraisData || []).forEach((f: any) => {
      if (f.contrat_st_id) {
        fraisParContrat[f.contrat_st_id] = (fraisParContrat[f.contrat_st_id] || 0) + Number(f.montant_ht || 0);
      }
    });
  }

  const montantTotal = contrats.reduce((sum, c) => sum + Number(c.montant_contrat_ht || 0), 0);
  const montantFacture = Object.values(fraisParContrat).reduce((sum, v) => sum + v, 0);
  const resteAPayer = Math.max(montantTotal - montantFacture, 0);

  if (import.meta.env.DEV) console.log('[useAnalyseIA] ✅ Sous-traitants:', contrats.length, '| Contrat:', montantTotal, '€ | Facturé:', montantFacture, '€ | Reste:', resteAPayer, '€');

  return {
    nombre: contrats.length,
    montant_total_ht: montantTotal,
    montant_facture_ht: montantFacture,
    reste_a_payer: resteAPayer,
    detail: contrats.map(c => {
      const st = c.sous_traitant as { nom: string; specialite: string } | null;
      const montantContrat = Number(c.montant_contrat_ht || 0);
      const montantFactureST = fraisParContrat[c.id] || 0;
      const resteAPayerST = Math.max(montantContrat - montantFactureST, 0);
      const pourcentagePaye = montantContrat > 0 ? Math.round((montantFactureST / montantContrat) * 100) : 0;

      return {
        nom: st?.nom || 'Inconnu',
        specialite: st?.specialite || '',
        montant_contrat_ht: montantContrat,
        montant_facture_ht: montantFactureST,
        reste_a_payer: resteAPayerST,
        pourcentage_paye: pourcentagePaye,
        statut: c.statut || 'a_demarrer',
        description: c.description_travaux || '',
      };
    }),
  };
}

// Helper pour charger les absences des membres de l'équipe pendant la période du chantier
async function chargerAbsences(
  membresIds: string[],
  dateDebutChantier: string | null,
  dateFinChantier: string | null
) {
  if (membresIds.length === 0 || !dateDebutChantier) {
    return {
      total_jours: 0,
      membres_avec_absences: 0,
      detail: [] as Array<{
        membre_nom: string;
        type_absence: string;
        date_debut: string;
        date_fin: string;
        jours: number;
        motif: string | null;
      }>,
    };
  }

  const dateFinEffective = dateFinChantier || new Date().toISOString().split('T')[0];

  const { data: absences } = await supabase
    .from('absences_equipe')
    .select('*, membre:membres_equipe(prenom, nom)')
    .in('membre_id', membresIds)
    .gte('date_fin', dateDebutChantier)
    .lte('date_debut', dateFinEffective);

  if (!absences || absences.length === 0) {
    return {
      total_jours: 0,
      membres_avec_absences: 0,
      detail: [],
    };
  }

  // Calculer le nombre de jours pour chaque absence
  const detail = absences.slice(0, 5).map(a => {
    const debut = new Date(a.date_debut);
    const fin = new Date(a.date_fin);
    const jours = Math.max(1, Math.ceil((fin.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    const membre = a.membre as { prenom: string; nom: string } | null;

    return {
      membre_nom: membre ? `${membre.prenom || ''} ${membre.nom || ''}`.trim() : 'Membre inconnu',
      type_absence: a.type_absence || 'autre',
      date_debut: a.date_debut,
      date_fin: a.date_fin,
      jours,
      motif: a.motif,
    };
  });

  const totalJours = absences.reduce((sum, a) => {
    const debut = new Date(a.date_debut);
    const fin = new Date(a.date_fin);
    return sum + Math.max(1, Math.ceil((fin.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24)) + 1);
  }, 0);

  const membresUniques = new Set(absences.map(a => a.membre_id));

  return {
    total_jours: totalJours,
    membres_avec_absences: membresUniques.size,
    detail,
  };
}

// Helper pour charger les conflits d'affectation (membres affectés sur d'autres chantiers)
async function chargerConflitsAffectation(
  chantierId: string,
  membresIds: string[],
  dateDebutChantier: string | null,
  dateFinChantier: string | null
) {
  if (membresIds.length === 0 || !dateDebutChantier) {
    return {
      nb_membres_en_conflit: 0,
      nb_total_membres: membresIds.length,
      detail: [] as Array<{
        membre_nom: string;
        autre_chantier_nom: string;
        heures_par_jour: number;
        date_debut: string;
        date_fin: string;
      }>,
    };
  }

  const dateFinEffective = dateFinChantier || new Date().toISOString().split('T')[0];

  const { data: autresAffectations } = await supabase
    .from('affectations_chantiers')
    .select('*, membre:membres_equipe(prenom, nom), chantier:chantiers(nom_chantier)')
    .in('membre_equipe_id', membresIds)
    .neq('chantier_id', chantierId)
    .gte('date_fin', dateDebutChantier)
    .lte('date_debut', dateFinEffective);

  if (!autresAffectations || autresAffectations.length === 0) {
    return {
      nb_membres_en_conflit: 0,
      nb_total_membres: membresIds.length,
      detail: [],
    };
  }

  const membresEnConflit = new Set(autresAffectations.map(a => a.membre_equipe_id));

  const detail = autresAffectations.slice(0, 5).map(a => {
    const membre = a.membre as { prenom: string; nom: string } | null;
    const chantier = a.chantier as { nom_chantier: string } | null;

    return {
      membre_nom: membre ? `${membre.prenom || ''} ${membre.nom || ''}`.trim() : 'Membre inconnu',
      autre_chantier_nom: chantier?.nom_chantier || 'Chantier inconnu',
      heures_par_jour: a.heures_par_jour || 8,
      date_debut: a.date_debut,
      date_fin: a.date_fin,
    };
  });

  return {
    nb_membres_en_conflit: membresEnConflit.size,
    nb_total_membres: membresIds.length,
    detail,
  };
}

// Helper pour charger les budgets par poste (depuis les ventilations si disponibles)
async function chargerBudgetsParPoste(
  chantierId: string,
  depensesMateriaux: number,
  depensesMainOeuvre: number,
  depensesFrais: number,
  montantDevisHT: number
): Promise<{
  budget_materiaux: number;
  budget_main_oeuvre: number;
  budget_frais: number;
  budget_total: number;
  reel_materiaux: number;
  reel_main_oeuvre: number;
  reel_frais: number;
  reel_total: number;
} | null> {
  // Chercher si des budgets par poste sont définis dans le chantier
  const { data: chantier } = await supabase
    .from('chantiers')
    .select('budget_materiaux, budget_main_oeuvre, budget_frais')
    .eq('id', chantierId)
    .single();

  // Si aucun budget par poste n'est défini, retourner null
  if (!chantier ||
      ((!chantier.budget_materiaux || chantier.budget_materiaux === 0) &&
       (!chantier.budget_main_oeuvre || chantier.budget_main_oeuvre === 0) &&
       (!chantier.budget_frais || chantier.budget_frais === 0))) {
    return null;
  }

  const budgetMateriaux = Number(chantier.budget_materiaux) || 0;
  const budgetMainOeuvre = Number(chantier.budget_main_oeuvre) || 0;
  const budgetFrais = Number(chantier.budget_frais) || 0;

  return {
    budget_materiaux: budgetMateriaux,
    budget_main_oeuvre: budgetMainOeuvre,
    budget_frais: budgetFrais,
    budget_total: budgetMateriaux + budgetMainOeuvre + budgetFrais,
    reel_materiaux: depensesMateriaux,
    reel_main_oeuvre: depensesMainOeuvre,
    reel_frais: depensesFrais,
    reel_total: depensesMateriaux + depensesMainOeuvre + depensesFrais,
  };
}

// ============================================================================
// FONCTION DE FORMATAGE POUR L'IA
// ============================================================================

interface DonneesFormateesPourIA {
  // Informations générales
  nom: string;
  reference: string | null;
  client: string;
  adresse: string;
  statut: string | null;
  is_termine: boolean;
  is_en_cours: boolean;

  // Planning
  date_debut: string | null;
  date_fin_prevue: string | null;
  date_fin_reelle: string | null;
  duree_prevue_jours: number;
  jours_ecoules: number;
  avancement_pct: number;
  ecart_jours: number;
  statut_planning: string;

  // Finances - Revenus
  montant_devis_ht: number;
  montant_devis_ttc: number;
  tva_pct: number;
  devis_version: string;
  devis_statut: string;

  // Finances - Dépenses
  depenses_materiaux: number;
  depenses_autres_factures: number;
  depenses_frais: number;
  depenses_main_oeuvre: number;
  depenses_total: number;
  detail_factures: Array<{ fournisseur: string; categorie: string; montant_ht: number; date: string }>;
  detail_frais: Array<{ type: string; description: string; montant: number; date: string }>;
  frais_par_type: Record<string, number>;

  // Finances - Marges
  marge_euros: number;
  marge_pct: number;
  budget_disponible: number;
  statut_rentabilite: string;

  // Équipe
  equipe_nb_membres: number;
  equipe_total_heures: number;
  equipe_cout_total: number;
  equipe_cout_horaire_moyen: number;
  equipe_detail: Array<{ nom: string; poste: string; jours: number; heures: number; cout: number }>;

  // Facturation client
  facture_total: number;
  paye_total: number;
  reste_a_facturer: number;
  reste_a_payer: number;
  taux_facturation: number;
  taux_encaissement: number;
  // Nouveau système simplifié
  statut_paiement: string;
  statut_paiement_label: string;

  // Indicateurs calculés
  cout_journalier: number;
  ca_par_jour: number;
  jour_critique: number | null;
  jours_restants_rentables: number | null;
  roi_pct: number;

  // Benchmarks
  benchmark_marge_min: number;
  benchmark_marge_max: number;
  benchmark_marge_moyenne: number;

  // Retards et Suspensions
  retard: {
    jours_retard: number;
    cout_retard_engage: number;
    cout_retard_previsionnel: number;
    penalites_contractuelles: number;
    cout_retard_total: number;
    impact_marge_euros: number;
  };
  suspension: {
    nombre_suspensions: number;
    duree_totale_jours: number;
    cout_remobilisation_total: number;
    suspensions_actives: number;
    raisons: string[];
    historique: Array<{
      date_suspension: string;
      date_reprise: string | null;
      raison: string;
      raison_detail: string | null;
      duree_jours: number;
      cout_mobilisation: number;
      type_suspension: string;
    }>;
  };

  // Absences de l'équipe
  absences: {
    total_jours: number;
    membres_avec_absences: number;
    detail: Array<{
      membre_nom: string;
      type_absence: string;
      date_debut: string;
      date_fin: string;
      jours: number;
    }>;
  } | null;

  // Conflits d'affectation
  conflits_affectation: {
    nb_membres_en_conflit: number;
    nb_total_membres: number;
    detail: Array<{
      membre_nom: string;
      autre_chantier_nom: string;
      heures_par_jour: number;
      date_debut: string;
      date_fin: string;
    }>;
  } | null;

  // Budgets par poste (pour clôture)
  budgets_par_poste: {
    budget_materiaux: number;
    budget_main_oeuvre: number;
    budget_frais: number;
    budget_total: number;
    reel_materiaux: number;
    reel_main_oeuvre: number;
    reel_frais: number;
    reel_total: number;
    ecart_materiaux_pct: number;
    ecart_main_oeuvre_pct: number;
    ecart_frais_pct: number;
  } | null;

  // Sous-traitants
  sous_traitants: {
    nombre: number;
    montant_total_ht: number;
    montant_facture_ht: number;
    reste_a_payer: number;
    detail: Array<{
      nom: string;
      specialite: string;
      montant_contrat_ht: number;
      montant_facture_ht: number;
      reste_a_payer: number;
      pourcentage_paye: number;
      statut: string;
    }>;
  } | null;
}

// Calculer automatiquement le statut de paiement depuis les données réelles
// Priorité aux paiements réels : un client peut payer sans qu'il y ait de facture formelle dans le système
function calculerStatutPaiement(facturation: {
  total_facture: number;
  total_paye: number;
  reste_a_payer: number;
}, montantDevis: number): string {
  // PRIORITÉ 1 : Vérifier d'abord les paiements réels (même sans facture formelle)
  if (facturation.total_paye >= montantDevis && montantDevis > 0) {
    return 'paye_integralement';
  }
  if (facturation.total_paye > 0) {
    // Moins de 30% = acompte, plus = paiement partiel
    return facturation.total_paye < montantDevis * 0.3 ? 'acompte_recu' : 'paye_partiellement';
  }
  // PRIORITÉ 2 : Si aucun paiement, vérifier les factures
  if (facturation.total_facture === 0) return 'non_facture';
  // Facturé mais rien encaissé
  if (facturation.total_paye === 0) return 'facture_envoyee';
  return 'non_facture';
}

// Helper pour convertir le statut de paiement en label lisible pour l'IA
function getStatutPaiementLabel(statut: string, montantPaye: number, montantDevis: number): string {
  const labels: Record<string, string> = {
    'non_facture': 'Non facturé',
    'facture_envoyee': 'Facture envoyée, en attente de paiement',
    'acompte_recu': `Acompte reçu (${montantPaye.toLocaleString('fr-FR')}€ sur ${montantDevis.toLocaleString('fr-FR')}€)`,
    'paye_partiellement': `Payé partiellement (${montantPaye.toLocaleString('fr-FR')}€ sur ${montantDevis.toLocaleString('fr-FR')}€)`,
    'paye_integralement': 'Payé intégralement',
    'en_retard': 'En retard de paiement',
    'impaye': 'Impayé / Contentieux',
  };
  return labels[statut] || 'Non renseigné';
}

function formaterDonneesPourIA(data: DonneesChantierCompletes, benchmarks: any): DonneesFormateesPourIA {
  const { chantier, devis, planning, depenses, equipe, facturation, metriques } = data;

  // Calculer l'avancement
  const aujourdhui = new Date();
  const dateDebut = planning.date_debut_reelle || planning.date_debut_prevue;
  const dateFin = planning.date_fin_prevue;

  let dureeTotal = planning.duree_estimee_jours || 1;
  let avancementPct = 0;
  let ecartJours = 0;

  if (dateDebut && dateFin) {
    const debut = new Date(dateDebut);
    const fin = new Date(dateFin);
    const dureeCalculee = Math.max(1, Math.ceil((fin.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24)));
    // PRIORITÉ : duree_estimee_jours > calcul depuis dates
    dureeTotal = planning.duree_estimee_jours || dureeCalculee;
    if (import.meta.env.DEV) console.log('[formaterDonneesPourIA] 📅 Durée estimée:', planning.duree_estimee_jours, '| Calculée:', dureeCalculee, '| Utilisée:', dureeTotal);
    avancementPct = Math.min(100, Math.round((planning.jours_ecoules / dureeTotal) * 100));

    // PRIORITÉ pour ecart_jours : retard déclaré > calcul calendaire
    const joursRetardDeclares = metriques.jours_retard_travailles || data.retards_enregistres?.jours_retard || 0;
    if (joursRetardDeclares > 0) {
      // Si retard déclaré via affectations retard, utiliser cette valeur
      ecartJours = joursRetardDeclares;
      if (import.meta.env.DEV) console.log('[formaterDonneesPourIA] ⚠️ Retard déclaré utilisé:', joursRetardDeclares, 'jours');
    } else if (planning.date_fin_reelle) {
      ecartJours = Math.ceil((new Date(planning.date_fin_reelle).getTime() - fin.getTime()) / (1000 * 60 * 60 * 24));
    } else {
      ecartJours = planning.jours_ecoules - dureeTotal;
    }
  }

  // Déterminer le statut du chantier
  const statutLower = chantier.statut?.toLowerCase() || '';
  const isTermine = ['termine', 'cloture', 'archived', 'cloturé'].includes(statutLower);
  const isEnCours = ['en_cours', 'planifie', 'devis_envoye', 'en cours'].includes(statutLower);

  // Déterminer le statut planning
  let statutPlanning = 'a_lheure';
  if (ecartJours < 0) statutPlanning = 'en_avance';
  else if (ecartJours > 5) statutPlanning = 'retard_important';
  else if (ecartJours > 2) statutPlanning = 'leger_retard';

  return {
    // Informations générales
    nom: chantier.nom,
    reference: chantier.reference,
    client: chantier.client || 'Non renseigné',
    adresse: chantier.adresse || 'Non renseignée',
    statut: chantier.statut,
    is_termine: isTermine,
    is_en_cours: isEnCours,

    // Planning
    date_debut: dateDebut,
    date_fin_prevue: dateFin,
    date_fin_reelle: planning.date_fin_reelle,
    duree_prevue_jours: dureeTotal,
    jours_ecoules: planning.jours_ecoules,
    avancement_pct: avancementPct,
    ecart_jours: ecartJours,
    statut_planning: statutPlanning,

    // Finances - Revenus
    montant_devis_ht: devis.montant_ht,
    montant_devis_ttc: devis.montant_ttc,
    tva_pct: devis.tva,
    devis_version: devis.version,
    devis_statut: devis.statut,

    // Finances - Dépenses
    depenses_materiaux: depenses.factures.materiaux,
    depenses_autres_factures: depenses.factures.autres,
    depenses_frais: depenses.frais.total,
    depenses_main_oeuvre: depenses.main_oeuvre.total,
    depenses_total: depenses.total,
    detail_factures: depenses.factures.detail.slice(0, 5),
    detail_frais: depenses.frais.detail.slice(0, 5),
    frais_par_type: depenses.frais.par_type,

    // Finances - Marges
    marge_euros: metriques.marge_euros,
    marge_pct: metriques.marge_pct,
    budget_disponible: metriques.budget_disponible,
    statut_rentabilite: metriques.statut_rentabilite,

    // Équipe
    equipe_nb_membres: equipe.nb_membres,
    equipe_total_heures: equipe.total_heures,
    equipe_cout_total: equipe.total_cout,
    equipe_cout_horaire_moyen: depenses.main_oeuvre.cout_horaire_moyen,
    equipe_detail: equipe.membres.map(m => ({
      nom: m.nom,
      poste: m.poste,
      jours: m.jours_travailles,
      heures: m.heures_total,
      cout: m.cout_total,
    })),

    // Facturation client
    facture_total: facturation.total_facture,
    paye_total: facturation.total_paye,
    reste_a_facturer: facturation.reste_a_facturer,
    reste_a_payer: facturation.reste_a_payer,
    taux_facturation: devis.montant_ht > 0 ? Math.round((facturation.total_facture / devis.montant_ht) * 100) : 0,
    taux_encaissement: facturation.pourcentage_encaisse ?? (devis.montant_ht > 0 ? Math.round((facturation.total_paye / devis.montant_ht) * 100) : 0),
    // Calculer automatiquement le statut depuis les paiements réels
    statut_paiement: calculerStatutPaiement(facturation, devis.montant_ht),
    statut_paiement_label: getStatutPaiementLabel(
      calculerStatutPaiement(facturation, devis.montant_ht),
      facturation.total_paye, // Utiliser le total payé réel, pas le champ manuel
      devis.montant_ht
    ),

    // Indicateurs calculés
    cout_journalier: metriques.cout_journalier,
    ca_par_jour: dureeTotal > 0 ? Math.round(devis.montant_ht / dureeTotal) : 0,
    jour_critique: metriques.jour_critique,
    jours_restants_rentables: metriques.jours_restants_rentables,
    roi_pct: depenses.total > 0 ? Math.round((metriques.marge_euros / depenses.total) * 100) : 0,

    // Benchmarks
    benchmark_marge_min: benchmarks?.min || 20,
    benchmark_marge_max: benchmarks?.max || 35,
    benchmark_marge_moyenne: benchmarks?.moyenne || 25,

    // Retards - Utilise les métriques SQL temps réel qui distinguent précisément engagé/prévisionnel
    retard: {
      // Jours de retard : nombre total de jours déclarés (même futurs)
      jours_retard: Math.max(
        data.retards_enregistres.jours_retard, // Jours de retard déclarés via l'onglet Retard
        ecartJours > 0 ? ecartJours : 0 // Écart planning positif = retard
      ),
      // Coût retard ENGAGÉ = depuis les métriques SQL (calcul précis des jours PASSÉS uniquement)
      cout_retard_engage: metriques.cout_retard_engage,
      // Coût retard PRÉVISIONNEL = depuis les métriques SQL (jours FUTURS + reste du jour courant)
      cout_retard_previsionnel: metriques.cout_retard_previsionnel,
      // Pénalités : combiner métriques + retards enregistrés
      penalites_contractuelles: metriques.penalites_retard + data.retards_enregistres.penalites_retard,
      // Total = engagé + prévisionnel + pénalités
      cout_retard_total: metriques.cout_retard_engage
        + metriques.cout_retard_previsionnel
        + metriques.penalites_retard
        + data.retards_enregistres.penalites_retard,
      // Impact sur marge = coûts déjà engagés + pénalités
      impact_marge_euros: metriques.cout_retard_engage
        + metriques.penalites_retard
        + data.retards_enregistres.penalites_retard,
    },

    // Suspensions
    suspension: {
      nombre_suspensions: data.suspensions.nombre,
      duree_totale_jours: data.suspensions.duree_totale_jours,
      cout_remobilisation_total: data.suspensions.cout_remobilisation_total,
      suspensions_actives: data.suspensions.actives,
      raisons: data.suspensions.raisons,
      historique: data.suspensions.historique,
    },

    // Absences (seulement si présentes)
    absences: data.absences.total_jours > 0 ? {
      total_jours: data.absences.total_jours,
      membres_avec_absences: data.absences.membres_avec_absences,
      detail: data.absences.detail.map(a => ({
        membre_nom: a.membre_nom,
        type_absence: a.type_absence,
        date_debut: a.date_debut,
        date_fin: a.date_fin,
        jours: a.jours,
      })),
    } : null,

    // Conflits d'affectation (seulement si présents)
    conflits_affectation: data.conflits_affectation.nb_membres_en_conflit > 0 ? {
      nb_membres_en_conflit: data.conflits_affectation.nb_membres_en_conflit,
      nb_total_membres: data.conflits_affectation.nb_total_membres,
      detail: data.conflits_affectation.detail,
    } : null,

    // Budgets par poste (seulement si configurés)
    budgets_par_poste: data.budgets_par_poste ? {
      budget_materiaux: data.budgets_par_poste.budget_materiaux,
      budget_main_oeuvre: data.budgets_par_poste.budget_main_oeuvre,
      budget_frais: data.budgets_par_poste.budget_frais,
      budget_total: data.budgets_par_poste.budget_total,
      reel_materiaux: data.budgets_par_poste.reel_materiaux,
      reel_main_oeuvre: data.budgets_par_poste.reel_main_oeuvre,
      reel_frais: data.budgets_par_poste.reel_frais,
      reel_total: data.budgets_par_poste.reel_total,
      ecart_materiaux_pct: data.budgets_par_poste.budget_materiaux > 0
        ? Math.round(((data.budgets_par_poste.reel_materiaux - data.budgets_par_poste.budget_materiaux) / data.budgets_par_poste.budget_materiaux) * 100)
        : 0,
      ecart_main_oeuvre_pct: data.budgets_par_poste.budget_main_oeuvre > 0
        ? Math.round(((data.budgets_par_poste.reel_main_oeuvre - data.budgets_par_poste.budget_main_oeuvre) / data.budgets_par_poste.budget_main_oeuvre) * 100)
        : 0,
      ecart_frais_pct: data.budgets_par_poste.budget_frais > 0
        ? Math.round(((data.budgets_par_poste.reel_frais - data.budgets_par_poste.budget_frais) / data.budgets_par_poste.budget_frais) * 100)
        : 0,
    } : null,

    // Sous-traitants
    sous_traitants: data.sous_traitants.nombre > 0 ? {
      nombre: data.sous_traitants.nombre,
      montant_total_ht: data.sous_traitants.montant_total_ht,
      montant_facture_ht: data.sous_traitants.montant_facture_ht,
      reste_a_payer: data.sous_traitants.reste_a_payer,
      detail: data.sous_traitants.detail.map(st => ({
        nom: st.nom,
        specialite: st.specialite,
        montant_contrat_ht: st.montant_contrat_ht,
        montant_facture_ht: st.montant_facture_ht,
        reste_a_payer: st.reste_a_payer,
        pourcentage_paye: st.pourcentage_paye,
        statut: st.statut,
      })),
    } : null,
  };
}

// ============================================================================
// PROMPT SYSTÈME OPTIMISÉ
// ============================================================================

const SYSTEM_PROMPT = `Tu es un expert-comptable et consultant spécialisé BTP avec 20 ans d'expérience auprès des TPE/PME du bâtiment en France.

MISSION : Analyser les données du chantier et fournir une analyse actionnable au dirigeant.

RÈGLES ABSOLUES :
1. UTILISE UNIQUEMENT les données fournies - ne jamais inventer
2. Si une donnée est à 0 ou manquante, signale-le comme point d'attention, pas comme erreur
3. Base tes calculs sur les chiffres fournis, pas sur des estimations
4. Sois CONCIS et DIRECT - le dirigeant BTP n'a pas le temps
5. Chaque recommandation doit être actionnable IMMÉDIATEMENT
6. Reste CONSTRUCTIF même si les résultats sont mauvais

ANALYSE DES MARGES :
- Marge < 10% = CRITIQUE (risque de perte)
- Marge 10-18% = ATTENTION (fragile, surveiller)
- Marge 18-25% = CORRECT (dans la moyenne BTP)
- Marge 25-35% = BON (au-dessus de la moyenne)
- Marge > 35% = EXCELLENT (mais vérifier si réaliste)

⚠️ IMPORTANT : Une marge > 50% est SUSPECTE dans le BTP.
Si tu vois une marge anormalement élevée (>50%), c'est probablement que :
- Des coûts n'ont pas encore été saisis
- Le chantier vient de démarrer
- Il y a une erreur de saisie
Dans ce cas, signale-le comme "données incomplètes" plutôt qu'une excellente performance.

SCORING (0-100) :
Le score doit refléter la QUALITÉ GLOBALE du chantier :
- Marge réelle vs objectif (40 points)
- Respect des délais (20 points)
- Complétude des données (20 points)
- Facturation/encaissement (10 points)
- Organisation équipe (10 points)

Pénalités :
- Données manquantes (équipe, factures) : -15 à -25 points
- Marge irréaliste (>60%) sans explication : -20 points
- Retard > 5 jours : -10 points

ANALYSE DES RETARDS ET SUSPENSIONS :
- Si retard > 0 jours : Analyser l'impact financier et proposer des actions correctives
- Si suspensions > 0 : Évaluer l'impact sur le planning et la rentabilité
- Pénalités de retard : Vérifier si elles sont proportionnées au préjudice
- Coût remobilisation : Comparer au coût journalier équipe (ne devrait pas dépasser 2x)
- Si suspension ACTIVE : ALERTE CRITIQUE, recommander action immédiate
- Raison "intemperies" : Facteur externe, suggérer provision pour aléas
- Raison "attente_materiaux" : Problème supply chain, recommander anticipation
- Raison "probleme_client" : Risque relation client, recommander documentation

ANALYSE DES ABSENCES ET CONFLITS :
- Si des absences sont signalées pendant la période du chantier, évaluer l'impact sur le planning
- Si des membres sont en double affectation (affectations parallèles sur DIFFÉRENTS chantiers), alerter sur le risque de surcharge
- Ne pas pénaliser le score si les absences sont gérées (congés planifiés = anticipation OK)
- Si plus de 50% des membres ont des conflits d'affectation, c'est une ALERTE CRITIQUE

⚠️ IMPORTANT - DOUBLONS LÉGITIMES SUR LE MÊME CHANTIER :
- Si un membre apparaît 2 fois sur le MÊME chantier avec des PÉRIODES DIFFÉRENTES, c'est NORMAL quand il y a un retard déclaré
- Affectation "période initiale" + affectation "période retard" = comportement ATTENDU, PAS une erreur
- NE PAS signaler comme erreur de saisie si les dates sont différentes et cohérentes avec un retard
- SEULEMENT signaler si les MÊMES dates exactes sont dupliquées (vraie erreur de saisie)

ANALYSE CLÔTURE (si chantier TERMINÉ) :
- Si le bilan budget par poste est fourni, analyser les écarts significatifs (>15%)
- Identifier les postes sous-estimés pour améliorer les futurs devis
- Si un poste a un budget prévu mais 0€ dépensé, signaler comme "poste prévu non utilisé" (peut indiquer un changement de plan ou une erreur de chiffrage)
- Fournir une recommandation concrète pour les prochains chantiers similaires basée sur les écarts observés
- Pour la clôture, le conseil_expert doit inclure une "leçon apprise" actionnable

FORMAT DE RÉPONSE : JSON uniquement, sans markdown, sans backticks.`;

// ============================================================================
// CONSTRUCTION DU PROMPT UTILISATEUR
// ============================================================================

function buildUserPrompt(data: DonneesFormateesPourIA): string {
  const formatNumber = (n: number) => n.toLocaleString('fr-FR');

  return `CHANTIER À ANALYSER
═══════════════════════════════════════════════════════════════

📋 INFORMATIONS GÉNÉRALES
Nom : ${data.nom}
Référence : ${data.reference || 'Non définie'}
Client : ${data.client}
Adresse : ${data.adresse}
Statut : ${data.statut || 'Non défini'}

📅 PLANNING
Date début : ${data.date_debut || 'Non définie'}
Date fin prévue : ${data.date_fin_prevue || 'Non définie'}
${data.date_fin_reelle ? `Date fin réelle : ${data.date_fin_reelle}` : ''}
Durée prévue : ${data.duree_prevue_jours} jours
Jours écoulés : ${data.jours_ecoules}
Avancement estimé : ${data.avancement_pct}%
Écart planning : ${data.ecart_jours > 0 ? '+' : ''}${data.ecart_jours} jours (${data.statut_planning})
${data.retard.jours_retard > 0 ? `⚠️ RETARD DÉCLARÉ : ${data.retard.jours_retard} jour(s) via affectations retard (les membres peuvent apparaître 2 fois : période initiale + retard)` : ''}

💰 FINANCES
┌─────────────────────────────────────────────┐
│ REVENUS                                     │
│ Devis HT : ${formatNumber(data.montant_devis_ht)} €                        │
│ Devis TTC : ${formatNumber(data.montant_devis_ttc)} € (TVA ${data.tva_pct}%)               │
│ Version : ${data.devis_version} | Statut : ${data.devis_statut}         │
├─────────────────────────────────────────────┤
│ DÉPENSES ENGAGÉES                           │
│ • Matériaux (factures) : ${formatNumber(data.depenses_materiaux)} €            │
│ • Autres factures : ${formatNumber(data.depenses_autres_factures)} €               │
│ • Frais annexes : ${formatNumber(data.depenses_frais)} €                  │
│ • Main d'œuvre : ${formatNumber(data.depenses_main_oeuvre)} €                 │
│ ─────────────────────────────────────────── │
│ TOTAL DÉPENSES : ${formatNumber(data.depenses_total)} €                   │
├─────────────────────────────────────────────┤
│ RÉSULTAT                                    │
│ MARGE : ${formatNumber(data.marge_euros)} € (${data.marge_pct.toFixed(1)}%)                    │
│ Statut : ${data.statut_rentabilite}                              │
│ Budget disponible : ${formatNumber(data.budget_disponible)} €             │
└─────────────────────────────────────────────┘

${data.detail_factures.length > 0 ? `
📄 DÉTAIL FACTURES FOURNISSEURS (${data.detail_factures.length})
${data.detail_factures.map(f => `• ${f.fournisseur} (${f.categorie}) : ${f.montant_ht}€`).join('\n')}
` : '⚠️ Aucune facture fournisseur enregistrée'}

${data.detail_frais.length > 0 ? `
📦 DÉTAIL FRAIS ANNEXES
${Object.entries(data.frais_par_type).map(([type, montant]) => `• ${type} : ${montant}€`).join('\n')}
` : ''}

👷 ÉQUIPE
${data.equipe_nb_membres > 0 ? `
Membres affectés : ${data.equipe_nb_membres}
Total heures : ${data.equipe_total_heures}h
Coût total MO : ${formatNumber(data.equipe_cout_total)} €
Coût horaire moyen : ${data.equipe_cout_horaire_moyen} €/h

Détail :
${data.equipe_detail.map(m => `• ${m.nom} (${m.poste}) : ${m.jours}j = ${m.heures}h → ${formatNumber(m.cout)}€`).join('\n')}
${data.retard.jours_retard > 0 ? `
📝 Note : Un même membre peut apparaître plusieurs fois dans la liste ci-dessus s'il est affecté à la fois sur la période initiale ET sur les jours de retard. Ce n'est PAS un problème - c'est une affectation intentionnelle pour assurer la continuité de l'équipe pendant le retard.` : ''}
` : '⚠️ Aucun membre d\'équipe affecté à ce chantier'}
${data.absences ? `
⚠️ ABSENCES ÉQUIPE PENDANT LE CHANTIER
${data.absences.detail.map(a => {
  const typeLabels: Record<string, string> = {
    conges: 'Congés',
    maladie: 'Arrêt maladie',
    formation: 'Formation',
    autre: 'Autre absence',
  };
  return `• ${a.membre_nom} : ${typeLabels[a.type_absence] || a.type_absence} du ${a.date_debut} au ${a.date_fin} (${a.jours} jours)`;
}).join('\n')}
Total jours d'absence : ${data.absences.total_jours} jours
` : ''}${data.conflits_affectation ? `
🔄 AFFECTATIONS PARALLÈLES
Membres aussi affectés sur d'autres chantiers pendant cette période :
${data.conflits_affectation.detail.map(c => `• ${c.membre_nom} : aussi sur "${c.autre_chantier_nom}" (${c.heures_par_jour}h/jour) du ${c.date_debut} au ${c.date_fin}`).join('\n')}
Nombre de membres en double affectation : ${data.conflits_affectation.nb_membres_en_conflit}/${data.conflits_affectation.nb_total_membres}
` : ''}${data.sous_traitants ? `
🔧 SOUS-TRAITANTS
Nombre de contrats : ${data.sous_traitants.nombre}
Montant total prévu : ${formatNumber(data.sous_traitants.montant_total_ht)} € HT
Montant déjà payé : ${formatNumber(data.sous_traitants.montant_facture_ht)} € HT
Reste à payer : ${formatNumber(data.sous_traitants.reste_a_payer)} € HT
${data.sous_traitants.detail.map(st => {
  const statutLabels: Record<string, string> = {
    a_demarrer: 'À démarrer',
    en_cours: 'En cours',
    termine: 'Terminé',
    annule: 'Annulé',
  };
  return `• ${st.nom} (${st.specialite || 'Non spécifié'}) : Contrat ${formatNumber(st.montant_contrat_ht)}€ | Payé ${formatNumber(st.montant_facture_ht)}€ (${st.pourcentage_paye}%) | Reste ${formatNumber(st.reste_a_payer)}€ - ${statutLabels[st.statut] || st.statut}`;
}).join('\n')}
` : ''}
${(data.retard.jours_retard > 0 || data.retard.cout_retard_total > 0 || data.ecart_jours > 0 || data.suspension.nombre_suspensions > 0) ? `
🔴 RETARDS ET SUSPENSIONS
${(data.retard.jours_retard > 0 || data.retard.cout_retard_total > 0 || data.ecart_jours > 0) ? `
RETARDS :
- Jours de retard : ${data.retard.jours_retard > 0 ? data.retard.jours_retard : data.ecart_jours} jours
- Coût retard engagé : ${formatNumber(data.retard.cout_retard_engage)} €
- Coût retard prévisionnel : ${formatNumber(data.retard.cout_retard_previsionnel)} €
- Pénalités contractuelles : ${formatNumber(data.retard.penalites_contractuelles)} €
- TOTAL impact retard : ${formatNumber(data.retard.cout_retard_total)} €
- Impact sur marge : -${formatNumber(data.retard.impact_marge_euros)} €
` : ''}
${data.suspension.nombre_suspensions > 0 ? `
SUSPENSIONS :
- Nombre de suspensions : ${data.suspension.nombre_suspensions}
- Durée totale : ${data.suspension.duree_totale_jours} jours
- Suspensions actives : ${data.suspension.suspensions_actives}${data.suspension.suspensions_actives > 0 ? ' ⚠️ CHANTIER SUSPENDU' : ''}
- Coût remobilisation total : ${formatNumber(data.suspension.cout_remobilisation_total)} €
- Raisons : ${data.suspension.raisons.map(r => {
  const labels: Record<string, string> = {
    intemperies: 'Intempéries',
    attente_materiaux: 'Attente matériaux',
    probleme_client: 'Problème client',
    autre: 'Autre',
  };
  return labels[r] || r;
}).join(', ') || 'Non renseignées'}

Historique suspensions :
${data.suspension.historique.map((s, i) => {
  const labels: Record<string, string> = {
    intemperies: 'Intempéries',
    attente_materiaux: 'Attente matériaux',
    probleme_client: 'Problème client',
    autre: 'Autre',
  };
  return `  ${i + 1}. ${s.date_suspension} → ${s.date_reprise || 'EN COURS'} (${s.duree_jours}j) - ${labels[s.raison] || s.raison}${s.raison_detail ? ': ' + s.raison_detail : ''}`;
}).join('\n')}
` : ''}
` : ''}
💳 PAIEMENT CLIENT
Statut : ${data.statut_paiement_label}
${data.statut_paiement !== 'paye_integralement' && data.statut_paiement !== 'non_facture' ? `Montant encaissé : ${formatNumber(data.paye_total)} € sur ${formatNumber(data.montant_devis_ht)} € (${data.taux_encaissement}%)` : ''}
${data.statut_paiement === 'non_facture' ? '⚠️ Aucune facture client émise' : ''}
${data.statut_paiement === 'en_retard' ? '🚨 ALERTE : Paiement en retard !' : ''}
${data.statut_paiement === 'impaye' ? '🚨 ALERTE CRITIQUE : Impayé / Contentieux !' : ''}

📊 INDICATEURS CALCULÉS
- ROI : ${data.roi_pct}%
- CA/jour : ${formatNumber(data.ca_par_jour)} €
- Coût journalier équipe : ${formatNumber(data.cout_journalier)} €
${data.jour_critique ? `- Jour critique (marge nulle) : Jour ${data.jour_critique}` : ''}
${data.jours_restants_rentables !== null ? `- Jours restants rentables : ${data.jours_restants_rentables}` : ''}

📈 BENCHMARKS BTP FRANCE
- Marge moyenne : ${data.benchmark_marge_min}-${data.benchmark_marge_max}% (objectif ${data.benchmark_marge_moyenne}%)
- Votre marge : ${data.marge_pct.toFixed(1)}% → ${data.marge_pct >= data.benchmark_marge_moyenne ? '✅ Au-dessus' : data.marge_pct >= data.benchmark_marge_min ? '➖ Dans la moyenne' : '⚠️ En-dessous'}
${(data.is_termine && data.budgets_par_poste) ? `
📊 BILAN BUDGET PAR POSTE DE DÉPENSE
┌──────────────────┬────────────┬────────────┬────────────┬──────────┐
│ Poste            │ Budget     │ Réel       │ Écart      │ Statut   │
├──────────────────┼────────────┼────────────┼────────────┼──────────┤
│ Main d'œuvre     │ ${formatNumber(data.budgets_par_poste.budget_main_oeuvre)} €    │ ${formatNumber(data.budgets_par_poste.reel_main_oeuvre)} €    │ ${data.budgets_par_poste.ecart_main_oeuvre_pct > 0 ? '+' : ''}${data.budgets_par_poste.ecart_main_oeuvre_pct}%    │ ${data.budgets_par_poste.ecart_main_oeuvre_pct <= 0 ? '✅ OK' : data.budgets_par_poste.ecart_main_oeuvre_pct <= 10 ? '⚠️ Léger' : '🔴 Dépas.'}    │
│ Matériaux        │ ${formatNumber(data.budgets_par_poste.budget_materiaux)} €    │ ${formatNumber(data.budgets_par_poste.reel_materiaux)} €    │ ${data.budgets_par_poste.ecart_materiaux_pct > 0 ? '+' : ''}${data.budgets_par_poste.ecart_materiaux_pct}%    │ ${data.budgets_par_poste.ecart_materiaux_pct <= 0 ? '✅ OK' : data.budgets_par_poste.ecart_materiaux_pct <= 10 ? '⚠️ Léger' : '🔴 Dépas.'}    │
│ Frais annexes    │ ${formatNumber(data.budgets_par_poste.budget_frais)} €    │ ${formatNumber(data.budgets_par_poste.reel_frais)} €    │ ${data.budgets_par_poste.ecart_frais_pct > 0 ? '+' : ''}${data.budgets_par_poste.ecart_frais_pct}%    │ ${data.budgets_par_poste.ecart_frais_pct <= 0 ? '✅ OK' : data.budgets_par_poste.ecart_frais_pct <= 10 ? '⚠️ Léger' : '🔴 Dépas.'}    │
├──────────────────┼────────────┼────────────┼────────────┼──────────┤
│ TOTAL            │ ${formatNumber(data.budgets_par_poste.budget_total)} €    │ ${formatNumber(data.budgets_par_poste.reel_total)} €    │ ${data.budgets_par_poste.reel_total <= data.budgets_par_poste.budget_total ? '-' : '+'}${formatNumber(Math.abs(data.budgets_par_poste.reel_total - data.budgets_par_poste.budget_total))} €    │          │
└──────────────────┴────────────┴────────────┴────────────┴──────────┘
${data.budgets_par_poste.budget_main_oeuvre > 0 && data.budgets_par_poste.reel_main_oeuvre === 0 ? '⚠️ Budget MO prévu non utilisé' : ''}
${data.budgets_par_poste.budget_materiaux > 0 && data.budgets_par_poste.reel_materiaux === 0 ? '⚠️ Budget Matériaux prévu non utilisé' : ''}
${data.budgets_par_poste.budget_frais > 0 && data.budgets_par_poste.reel_frais === 0 ? '⚠️ Budget Frais prévu non utilisé' : ''}
` : ''}
═══════════════════════════════════════════════════════════════
CONTEXTE : Chantier ${data.is_termine ? 'TERMINÉ (bilan final)' : data.is_en_cours ? 'EN COURS (analyse prévisionnelle)' : 'EN PRÉPARATION'}
═══════════════════════════════════════════════════════════════

GÉNÈRE L'ANALYSE AU FORMAT JSON (sans backticks, sans markdown) :

{
  "score": <0-100>,
  "etoiles": <1-5>,
  "verdict": "<1-2 phrases de synthèse>",
  "indicateur": "<exceptionnel|excellent|tres_bien|bien|correct|attention|critique>",

  "sante_financiere": {
    "marge_actuelle_pct": ${data.marge_pct.toFixed(1)},
    "vs_benchmark": "<au-dessus|dans_moyenne|en-dessous>",
    "statut": "<excellent|bon|attention|critique>",
    "alerte": "<message si problème ou null>",
    "analyse": "<1 phrase d'analyse>"
  },

  "sante_planning": {
    "avancement_pct": ${data.avancement_pct},
    "ecart_jours": ${data.ecart_jours},
    "statut": "<en_avance|a_lheure|leger_retard|retard_critique>",
    "alerte": "<message si problème ou null>"
  },

  "completude_donnees": {
    "score": <0-100>,
    "manquants": ["<liste des données manquantes>"],
    "impact": "<comment ça affecte l'analyse>"
  },

  "points_forts": ["<max 3 points forts>"],

  "alertes": [
    {
      "niveau": "<critique|attention|info>",
      "titre": "<titre court>",
      "message": "<description>",
      "action": "<que faire>"
    }
  ],

  "recommandations": [
    {
      "priorite": "<haute|moyenne|basse>",
      "action": "<action concrète>",
      "impact": "<résultat attendu>",
      "categorie": "<marge|cout|planning|facturation|organisation>"
    }
  ],

  "kpis": {
    "roi_pct": ${data.roi_pct},
    "ca_par_jour": ${data.ca_par_jour},
    "marge_euros": ${data.marge_euros.toFixed(2)},
    "marge_pct": ${data.marge_pct.toFixed(1)},
    "cout_journalier": ${data.cout_journalier}
  },

  "conseil_expert": "<2-3 phrases de conseil personnalisé>"
}`;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export function useAnalyseIA(chantierId: string, entrepriseId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ============================================================================
  // QUERY : État (dernière analyse + limites)
  // ============================================================================

  const { data: etatData, isLoading: loadingLimites } = useQuery({
    queryKey: queryKeys.analyseIA.chantier(entrepriseId!, chantierId),
    queryFn: async () => {
      let derniereAnalyseResult: AnalyseRecord | null = null;
      let analyseResult: AnalyseIAResult | null = null;
      let limitesResult: LimitesAnalyse;

      // Récupérer la dernière analyse
      const { data: lastAnalyse, error: lastError } = await supabase
        .rpc('derniere_analyse_chantier', { p_chantier_id: chantierId });

      if (!lastError && lastAnalyse) {
        derniereAnalyseResult = lastAnalyse as AnalyseRecord;
        analyseResult = lastAnalyse.resultat as AnalyseIAResult;
      }

      // Vérifier le cooldown
      const { data: cooldownData, error: cooldownError } = await supabase
        .rpc('peut_analyser_chantier', { p_chantier_id: chantierId });

      // Vérifier le quota
      const plan = 'starter';
      const { data: quotaData, error: quotaError } = await supabase
        .rpc('verifier_quota_analyse', { p_entreprise_id: entrepriseId!, p_plan: plan });

      if (!cooldownError && !quotaError && cooldownData && quotaData) {
        limitesResult = {
          peutAnalyser: cooldownData.peut_analyser && quotaData.peut_analyser,
          minutesRestantes: Math.ceil(cooldownData.minutes_restantes || 0),
          quotaMax: quotaData.quota_max,
          quotaUtilise: quotaData.utilisees,
          quotaRestant: quotaData.restantes,
        };
      } else {
        limitesResult = {
          peutAnalyser: true,
          minutesRestantes: 0,
          quotaMax: 30,
          quotaUtilise: 0,
          quotaRestant: 30,
        };
      }

      return {
        derniereAnalyse: derniereAnalyseResult,
        analyse: analyseResult,
        limites: limitesResult,
      };
    },
    enabled: !!user && !!chantierId && !!entrepriseId,
    staleTime: 10 * 60 * 1000, // 10 min — une analyse ne change pas seule
  });

  // ============================================================================
  // QUERY : Historique des analyses
  // ============================================================================

  const { data: historique = [] } = useQuery({
    queryKey: [...queryKeys.analyseIA.chantier(entrepriseId!, chantierId), 'historique'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('analyses_ia')
        .select('id, score, resultat, created_at, type')
        .eq('chantier_id', chantierId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erreur chargement historique analyses:', error);
        throw error;
      }

      return (data || []) as AnalyseRecord[];
    },
    enabled: !!chantierId && !!entrepriseId,
    staleTime: 10 * 60 * 1000, // 10 min
  });

  // Dériver les données depuis les queries
  const derniereAnalyse = etatData?.derniereAnalyse ?? null;
  const limites = etatData?.limites ?? null;

  // ============================================================================
  // MUTATION : Générer une analyse
  // ============================================================================

  const analyseMutation = useMutation({
    mutationFn: async ({ type }: { type: 'manuelle' | 'cloture' }) => {
      // 1. Charger TOUTES les données depuis Supabase
      if (import.meta.env.DEV) console.log('[useAnalyseIA] 🔄 Chargement des données complètes...');
      const donnees = await chargerDonneesCompletes(chantierId);

      // 2. Récupérer les benchmarks
      const typeChantier = getTypeBenchmarkFromPictogramme(donnees.chantier.pictogramme || 'Home');
      const benchmarkNational = BENCHMARKS_NATIONAUX.marges[typeChantier as keyof MargesParType];
      const regionData = donnees.chantier.region ? BENCHMARKS_REGIONS[donnees.chantier.region] : null;

      const benchmarks = {
        min: benchmarkNational?.min || regionData?.marges_ajustees?.min || 20,
        max: benchmarkNational?.max || regionData?.marges_ajustees?.max || 35,
        moyenne: benchmarkNational?.moyenne || regionData?.marges_ajustees?.moyenne || 25,
      };

      // 3. Formater les données pour l'IA
      const donneesFormatees = formaterDonneesPourIA(donnees, benchmarks);
      if (import.meta.env.DEV) console.log('[useAnalyseIA] 📊 Données formatées pour l\'IA:', donneesFormatees);

      // 4. Construire les prompts
      const userPrompt = buildUserPrompt(donneesFormatees);
      if (import.meta.env.DEV) console.log('[useAnalyseIA] 📝 Prompt utilisateur généré');

      // 5. Appeler l'Edge Function
      const { data, error: fnError } = await supabase.functions.invoke('analyze-chantier', {
        body: {
          chantierData: donneesFormatees,
          systemPrompt: SYSTEM_PROMPT,
          userPrompt: userPrompt,
          type,
        },
      });

      if (fnError) throw new Error(fnError.message);
      if (!data?.success) throw new Error(data?.error || 'Erreur lors de l\'analyse');

      // 6. Enregistrer l'analyse
      try {
        await supabase
          .from('analyses_ia')
          .insert({
            chantier_id: chantierId,
            entreprise_id: entrepriseId!,
            type,
            score: data.analysis.score,
            resultat: data.analysis,
            tokens_input: data.tokens?.input || 0,
            tokens_output: data.tokens?.output || 0,
            cout_estime: ((data.tokens?.input || 0) * 0.003 + (data.tokens?.output || 0) * 0.015) / 1000,
          });
      } catch (dbErr) {
        console.error('Erreur enregistrement analyse:', dbErr);
      }

      return data.analysis as AnalyseIAResult;
    },
    onSuccess: () => {
      toast.success('Analyse générée avec succès !');
      // Invalider état + historique
      invalidateAnalyseIA(queryClient, entrepriseId!, chantierId);

      // Émettre un événement pour auto-dismiss de la notification "Analyse IA disponible"
      window.dispatchEvent(new CustomEvent('analyse-ia-complete', {
        detail: { chantierId }
      }));
    },
    onError: (err) => {
      const error = err instanceof Error ? err : new Error(String(err));
      const message = error.message;

      captureError(error, {
        context: 'analyse_ia_generate',
        chantierId,
        entrepriseId,
        type: 'manuelle'
      });

      // Message plus explicite selon le type d'erreur
      if (message.includes('timeout') || message.includes('network')) {
        toast.error('Problème de connexion', {
          description: 'L\'analyse n\'a pas pu aboutir. Vérifiez votre connexion et réessayez.'
        });
      } else if (message.includes('quota') || message.includes('limit')) {
        toast.warning('Limite atteinte', {
          description: message
        });
      } else {
        toast.error('Erreur lors de l\'analyse', {
          description: 'Une erreur technique s\'est produite. Réessayez dans quelques instants.'
        });
      }
    },
  });

  // Wrapper pour garder l'API identique
  const genererAnalyse = async (
    _chantierComplet: any,
    type: 'manuelle' | 'cloture' = 'manuelle'
  ): Promise<AnalyseIAResult | null> => {
    if (!user || !entrepriseId) {
      toast.error('Session expirée', { description: 'Reconnectez-vous pour lancer une analyse.' });
      return null;
    }

    // Vérifier les limites
    if (type === 'manuelle' && limites) {
      if (!limites.peutAnalyser) {
        if (limites.minutesRestantes > 0) {
          const heures = Math.floor(limites.minutesRestantes / 60);
          const minutes = Math.round(limites.minutesRestantes % 60);
          toast.warning('Analyse temporairement indisponible', {
            description: `Prochaine analyse dans ${heures}h${minutes.toString().padStart(2, '0')}. Temps d'attente entre deux analyses.`
          });
        } else if (limites.quotaRestant <= 0) {
          toast.warning('Quota mensuel atteint', {
            description: `Vous avez utilisé vos ${limites.quotaMax} analyses du mois. Passez au forfait supérieur pour plus d'analyses.`
          });
        }
        return null;
      }
    }

    try {
      return await analyseMutation.mutateAsync({ type });
    } catch {
      return null;
    }
  };

  // Analyse en cours = dernière mutation réussie OU dernière analyse depuis DB
  const analyse = analyseMutation.data ?? etatData?.analyse ?? null;

  // Formater le temps restant
  const formatTempsRestant = useCallback(() => {
    if (!limites || limites.minutesRestantes <= 0) return null;
    const heures = Math.floor(limites.minutesRestantes / 60);
    const minutes = Math.round(limites.minutesRestantes % 60);
    return `${heures}h${minutes.toString().padStart(2, '0')}`;
  }, [limites]);

  return {
    analyse,
    derniereAnalyse,
    historique,
    limites,
    isLoading: analyseMutation.isPending,
    loadingLimites,
    error: analyseMutation.error?.message ?? null,
    genererAnalyse,
    formatTempsRestant,
    rafraichir: async () => { invalidateAnalyseIA(queryClient, entrepriseId!, chantierId); },
    rafraichirHistorique: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.analyseIA.chantier(entrepriseId!, chantierId), 'historique'],
      });
    },
  };
}
